<template>
  <v-card class="elevation-4">
    <v-card-title class="title-card">
      <h4>
        <v-icon size="18" class="mx-2">fa-solid fa-star</v-icon>
        {{ $t("components.views.settings.groups.manage_priorities") }}
      </h4>
      <v-spacer />
      <v-btn depressed text color="white" @click.stop="$emit('closeModal')">
        <v-icon small class="mr-1">fa-regular fa-rectangle-xmark</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-container fluid>
        <!-- Tabs para las diferentes categorías -->
        <v-tabs v-model="tab" fixed-tabs>
          <v-tabs-slider color="#FF6B35"></v-tabs-slider>
          <v-tab href="#transportCompanies" active-class="primary white--text">
            <v-icon size="14" class="mr-2">fa-solid fa-building</v-icon>
            {{ $t("components.views.settings.groups.transport_companies") }}
          </v-tab>
          <v-tab href="#drivers" active-class="primary white--text">
            <v-icon size="14" class="mr-2">fa-solid fa-user</v-icon>
            {{ $t("components.views.settings.groups.drivers") }}
          </v-tab>
          <v-tab href="#vehicles" active-class="primary white--text">
            <v-icon size="14" class="mr-2">fa-solid fa-id-card</v-icon>
            {{ $t("components.views.settings.groups.vehicles") }}
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <!-- Empresas de Transporte -->
          <v-tab-item value="transportCompanies">
            <v-card flat>
              <v-card-text class="pa-0">
                <v-data-table
                  :headers="transportCompanyHeaders"
                  :items="members.transportCompanies"
                  :items-per-page="10"
                  :no-data-text="$t('components.views.settings.groups.no_transport_companies')"
                  hide-default-footer
                  v-if="members.transportCompanies && members.transportCompanies.length > 0"
                  class="elevation-1"
                >
                  <template v-slot:item.priority="{ item }">
                    <v-checkbox
                      v-model="item.priority"
                      @change="updatePriority('transportCompanies', item)"
                      color="#FF6B35"
                      hide-details
                    ></v-checkbox>
                  </template>
                </v-data-table>
                <v-card v-else class="text-center pa-8 elevation-1">
                  <v-icon size="48" color="grey lighten-1">fa-solid fa-building</v-icon>
                  <p class="mt-4 grey--text">{{ $t("components.views.settings.groups.no_transport_companies") }}</p>
                </v-card>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Conductores -->
          <v-tab-item value="drivers">
            <v-card flat>
              <v-card-text class="pa-0">
                <v-data-table
                  :headers="driverHeaders"
                  :items="members.drivers"
                  :items-per-page="10"
                  :no-data-text="$t('components.views.settings.groups.no_drivers')"
                  hide-default-footer
                  v-if="members.drivers && members.drivers.length > 0"
                  class="elevation-1"
                >
                  <template v-slot:item.priority="{ item }">
                    <v-checkbox
                      v-model="item.priority"
                      @change="updatePriority('drivers', item)"
                      color="#FF6B35"
                      hide-details
                    ></v-checkbox>
                  </template>
                </v-data-table>
                <v-card v-else class="text-center pa-8 elevation-1">
                  <v-icon size="48" color="grey lighten-1">fa-solid fa-user</v-icon>
                  <p class="mt-4 grey--text">{{ $t("components.views.settings.groups.no_drivers") }}</p>
                </v-card>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Vehículos -->
          <v-tab-item value="vehicles">
            <v-card flat>
              <v-card-text class="pa-0">
                <v-data-table
                  :headers="vehicleHeaders"
                  :items="members.vehicles"
                  :items-per-page="10"
                  :no-data-text="$t('components.views.settings.groups.no_vehicles')"
                  hide-default-footer
                  v-if="members.vehicles && members.vehicles.length > 0"
                  class="elevation-1"
                >
                  <template v-slot:item.priority="{ item }">
                    <v-checkbox
                      v-model="item.priority"
                      @change="updatePriority('vehicles', item)"
                      color="#FF6B35"
                      hide-details
                    ></v-checkbox>
                  </template>
                </v-data-table>
                <v-card v-else class="text-center pa-8 elevation-1">
                  <v-icon size="48" color="grey lighten-1">fa-solid fa-id-card</v-icon>
                  <p class="mt-4 grey--text">{{ $t("components.views.settings.groups.no_vehicles") }}</p>
                </v-card>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-container>
    </v-card-text>

    <v-divider />
    
    <v-card-actions class="d-flex justify-space-between pa-4">
      <v-btn color="grey" text class="rounded-lg" @click.stop="$emit('closeModal')">
        <v-icon small class="mr-1">fa-solid fa-times</v-icon>
        {{ $t("components.views.settings.groups.close") }}
      </v-btn>

      <v-btn 
        color="#FF6B35" 
        dark 
        class="rounded-lg" 
        @click="savePriorities"
        :loading="saving"
        :disabled="saving"
      >
        <v-icon small class="mr-1">fa-solid fa-save</v-icon>
        {{ $t("components.views.settings.groups.save_priorities") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "ModalPriorities",
  props: {
    groupData: {
      type: Object,
      required: true,
      default: () => ({
        id: "",
        name: "",
        members: {
          transportCompanies: [],
          drivers: [],
          vehicles: []
        }
      })
    },
    isEditMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tab: "transportCompanies",
      saving: false,
      members: {
        transportCompanies: [],
        drivers: [],
        vehicles: []
      }
    };
  },
  computed: {
    transportCompanyHeaders() {
      return [
        {
          text: this.$t("components.views.settings.groups.company_name"),
          align: "left",
          sortable: false,
          value: "name",
        },
        {
          text: this.$t("components.views.settings.groups.identification"),
          align: "center", 
          sortable: false,
          value: "identification",
        },
        {
          text: this.$t("components.views.settings.groups.nit"),
          align: "center",
          sortable: false,
          value: "nit",
        },
        {
          text: this.$t("components.views.settings.groups.priority"),
          align: "center",
          sortable: false,
          value: "priority",
          width: "120px"
        },
      ];
    },
    driverHeaders() {
      return [
        {
          text: this.$t("components.views.settings.groups.driver_name"),
          align: "left",
          sortable: false,
          value: "name",
        },
        {
          text: this.$t("components.views.settings.groups.identification"),
          align: "center",
          sortable: false,
          value: "identification",
        },
        {
          text: this.$t("components.views.settings.groups.priority"),
          align: "center",
          sortable: false,
          value: "priority",
          width: "120px"
        },
      ];
    },
    vehicleHeaders() {
      return [
        {
          text: this.$t("components.views.settings.groups.license_plate"),
          align: "center",
          sortable: false,
          value: "licensePlate",
        },
        {
          text: this.$t("components.views.settings.groups.priority"),
          align: "center",
          sortable: false,
          value: "priority",
          width: "120px"
        },
      ];
    }
  },
  watch: {
    groupData: {
      immediate: true,
      deep: true,
      handler(newValue) {
        console.log('ModalPriorities - Received groupData:', JSON.stringify(newValue, null, 2));
        
        if (newValue && newValue.members) {
          // Copiar y normalizar los datos, asegurando que priority siempre sea true o false
          this.members = {
            transportCompanies: (newValue.members.transportCompanies || []).map(tc => ({
              ...tc,
              priority: !!tc.priority
            })),
            drivers: (newValue.members.drivers || []).map(d => ({
              ...d,
              priority: !!d.priority
            })),
            vehicles: (newValue.members.vehicles || []).map(v => ({
              ...v,
              priority: !!v.priority
            }))
          };
          
          console.log('ModalPriorities - Set this.members:', JSON.stringify(this.members, null, 2));
        }
      }
    }
  },
  methods: {
    ...mapActions("snackbar", ["showSnack"]),
    ...mapActions("loader", ["showLoader"]),

    updatePriority(type, item) {
      // Actualizar localmente - el guardado se hace al hacer clic en "Guardar"
      console.log(`Priority updated for ${type}:`, item);
    },

    async savePriorities() {
      this.saving = true;
      try {
        // Asegurar que todos los miembros tengan la propiedad priority como true o false
        const normalizedMembers = {
          transportCompanies: (this.members.transportCompanies || []).map(tc => ({
            id: tc.id || 0,
            name: tc.name || "",
            identification: tc.identification || "",
            nit: tc.nit || "",
            priority: !!tc.priority
          })),
          drivers: (this.members.drivers || []).map(d => ({
            id: d.id || 0,
            name: d.name || "",
            identification: d.identification || "",
            priority: !!d.priority
          })),
          vehicles: (this.members.vehicles || []).map(v => ({
            id: v.id || 0,
            licensePlate: v.licensePlate || "",
            priority: !!v.priority
          }))
        };

        const updatedGroupData = {
          id: this.groupData.id || "",
          name: this.groupData.name || "",
          idCompany: this.groupData.idCompany || 0,
          members: normalizedMembers
        };

        console.log('Sending priorities to server:', JSON.stringify(updatedGroupData, null, 2));

        // Solo guardar en BD si estamos en modo EDICIÓN
        // Si estamos creando un nuevo grupo, solo actualizar localmente
        if (this.isEditMode && this.groupData.id) {
          await this.axios.post("/Groups/saveGroup", updatedGroupData, {
            headers: {
              "Content-Type": "application/json",
            },
          });

          this.showSnack({
            text: this.$t("components.views.settings.groups.priorities_saved_successfully"),
            title: this.$t("components.views.settings.groups.success"),
            name: "success",
          });
        } else {
          // Modo creación: solo actualizar localmente, no guardar en BD
          this.showSnack({
            text: "Prioridades configuradas. Haz clic en GUARDAR para crear el grupo.",
            title: "Información",
            name: "info",
          });
        }

        this.$emit("prioritiesUpdated", updatedGroupData);
        this.$emit("closeModal");
      } catch (error) {
        console.error("Error saving priorities:", error);
        this.showSnack({
          text: error.response?.data || this.$t("components.views.settings.groups.error_saving_priorities"),
          title: this.$t("components.views.settings.groups.error"),
          name: "error",
        });
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.title-card {
  background-color: #0b0045 !important;
  color: white !important;
}

.title-card >>> .fa-solid {
  color: #fff !important;
}

.rounded-lg {
  border-radius: 8px !important;
}

.v-tab--active {
  background-color: #0b0045 !important;
  color: white !important;
}

.v-data-table >>> .v-data-table__wrapper {
  max-height: 400px;
  overflow-y: auto;
}

.elevation-1 {
  box-shadow: 0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12) !important;
}

.v-tabs-slider {
  background-color: #FF6B35 !important;
}

.v-data-table >>> .v-data-table-header {
  background-color: #f5f5f5;
}

.v-data-table >>> th {
  font-weight: 600 !important;
  color: #0b0045 !important;
}

.v-card-actions {
  padding: 16px 20px;
  background-color: #f9f9f9;
}

.v-checkbox >>> .v-input--selection-controls__input {
  color: #FF6B35 !important;
}

.v-checkbox >>> .v-icon {
  color: #FF6B35 !important;
}

.v-tabs >>> .v-tab {
  font-weight: 500;
  text-transform: none;
}

.v-tabs >>> .v-tab:hover {
  background-color: rgba(11, 0, 69, 0.1);
}

.v-tab-item >>> .v-card {
  background-color: transparent !important;
}
</style>